// Pure helpers that turn Lextria Dashboard form-data (schema_version 1) into AutoIP page state.
// The dashboard sends values as stored ("Indian", "India", "YYYY-MM-DD"); the pages expect
// their own conventions, so every conversion lives here.

const NATIONALITY_CODES = {
  indian: "IN",
  india: "IN",
  american: "US",
  "united states": "US",
  "united states of america": "US",
  usa: "US",
  british: "GB",
  "united kingdom": "GB",
  uk: "GB",
  german: "DE",
  germany: "DE",
  french: "FR",
  france: "FR",
  japanese: "JP",
  japan: "JP",
  chinese: "CN",
  china: "CN",
  canadian: "CA",
  canada: "CA",
  australian: "AU",
  australia: "AU",
  singaporean: "SG",
  singapore: "SG",
};

const str = (v) => (typeof v === "string" ? v.trim() : "");

// "Indian" -> "IN"; unknown values are kept as typed so the user can still see them
export function toNationalityCode(value) {
  const v = str(value);
  if (!v) return "";
  return NATIONALITY_CODES[v.toLowerCase()] || v;
}

// Form135 uses country codes ("IN")
export function toCountryCode(value) {
  return toNationalityCode(value);
}

// Unified/Simple pages use country names ("India")
export function toCountryName(value) {
  const v = str(value);
  if (!v) return "";
  if (v.toUpperCase() === "IN") return "India";
  return v;
}

const CATEGORY_BY_ENTITY = {
  Individual: "Natural Person",
  Startup: "Startup",
  "Small Entity": "Small Entity",
  "Educational Institution": "Educational Institution",
  Corporate: "Other than Natural Person",
  Government: "Other than Natural Person",
  Other: "Others",
};

// First applicant's entity_type -> Form135 category ("" lets the user pick)
export function toCategory(applicants) {
  const first = Array.isArray(applicants) && applicants.length > 0 ? applicants[0] : null;
  return (first && CATEGORY_BY_ENTITY[str(first.entity_type)]) || "";
}

export function toSpecificationType(patentType) {
  switch (str(patentType)) {
    case "PS_ONLY":
      return "Provisional Specification";
    case "CS_ONLY":
    case "PS_TO_CS":
      return "Complete Specification";
    default:
      return "";
  }
}

const names = (list) =>
  (Array.isArray(list) ? list : []).map((p) => str(p && p.name)).filter(Boolean);

// "yes" only when inventor names exactly equal applicant names (same order)
export function toInventorsSame(applicants, inventors) {
  const a = names(applicants);
  const i = names(inventors);
  if (a.length === 0 || a.length !== i.length) return "no";
  return a.every((n, idx) => n === i[idx]) ? "yes" : "no";
}

// <input type="date"> takes YYYY-MM-DD; anything else becomes ""
export function toDateInput(value) {
  const v = str(value);
  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : "";
}

// Applicant rows for UnifiedFormPage / SimpleFormPage: {name, nationality:"IN", country:"India", address}
export function toUnifiedApplicants(applicants) {
  const list = Array.isArray(applicants) ? applicants : [];
  if (list.length === 0) return null;
  return list.map((a) => ({
    name: str(a.name),
    nationality: toNationalityCode(a.nationality) || "IN",
    country: toCountryName(a.country_of_residence) || "India",
    address: str(a.address),
  }));
}

// Applicant rows for Form135: {name, nationality:"IN", country:"IN", address}
export function toForm135Applicants(applicants) {
  const list = Array.isArray(applicants) ? applicants : [];
  if (list.length === 0) return null;
  return list.map((a) => ({
    name: str(a.name),
    nationality: toNationalityCode(a.nationality) || "IN",
    country: toCountryCode(a.country_of_residence) || "IN",
    address: str(a.address),
  }));
}

// Inventor rows for Form135: {name, gender, nationality, country, address}
export function toForm135Inventors(inventors) {
  const list = Array.isArray(inventors) ? inventors : [];
  if (list.length === 0) return null;
  return list.map((i) => ({
    name: str(i.name),
    gender: str(i.gender),
    nationality: toNationalityCode(i.nationality) || "IN",
    country: toCountryCode(i.country_of_residence) || "IN",
    address: str(i.address),
  }));
}

// Common fields shared by UnifiedFormPage and SimpleFormPage. Returns {} without prefill,
// and omits applicants when the dashboard sent none, so pages keep their default empty row.
export function toCommonFormFields(prefill) {
  if (!prefill) return {};
  const fields = {
    trackingNumber: str(prefill.tracking_id),
    creatorName: str(prefill.requested_by?.full_name),
    title: str(prefill.patent_title),
    applicationNo: str(prefill.application_no),
    applicationDate: toDateInput(prefill.date_of_filing),
  };
  const applicants = toUnifiedApplicants(prefill.applicants);
  if (applicants) fields.applicants = applicants;
  return fields;
}

// Initial state for Form135 (forms 1/3/5/28). Empty values mean "keep the page default".
export function toForm135Fields(prefill) {
  if (!prefill) return {};
  const fields = {
    trackingNumber: str(prefill.tracking_id),
    creatorName: str(prefill.requested_by?.full_name),
    title: str(prefill.patent_title),
    applicationNo: str(prefill.application_no),
    filingDate: toDateInput(prefill.date_of_filing),
    category: toCategory(prefill.applicants),
    inventorsSame: toInventorsSame(prefill.applicants, prefill.inventors),
    specificationType: toSpecificationType(prefill.patent_type),
  };
  const applicants = toForm135Applicants(prefill.applicants);
  if (applicants) fields.applicants = applicants;
  const inventors = toForm135Inventors(prefill.inventors);
  if (inventors) fields.inventors = inventors;
  return fields;
}
